import type { DocumentRef } from '../../lib/documentReader'
import { isBookDiscoveryCard } from '../navigation/context.js'
import type {
  BookDiscoveryCard,
  ContextWork,
  QueueCounts,
  RemovalWork,
  ViewScope,
  WorkCard,
} from '../navigation/context'
import type { BookReview } from '../rkdecks/bookReview'
import type { ManualBookRouting } from '../rkdecks/manualBookRouting'

export type ContextQueue = 'book' | 'evidence' | 'chase'

export interface ContextAnchor {
  kind: 'entity' | 'instrument' | 'position' | 'trade'
  id: string
}

export interface ContextProposal {
  manual_book_context?: ManualBookRouting | null
  id: string
  group_id: string
  proposal_type: string
  status: string
  target?: ContextAnchor | null
  data?: Record<string, unknown>
  conflict_flags?: readonly unknown[]
  reasoning?: string | null
  document_ids?: readonly string[]
  dependencies?: readonly string[]
}

export interface ContextRequirement {
  id: string
  target: ContextAnchor
  code: string
  label: string
  status: string
  queue?: ContextQueue | null
  evidence_state?: string
  linked_document_ids?: readonly string[]
  request_ids?: readonly string[]
  active_request_ids?: readonly string[]
  task_ids?: readonly string[]
  snoozed?: boolean
}

export interface ContextRequest {
  id: string
  requirement_id?: string | null
  fund_id: string
  status: string
  counterparty_name?: string | null
  fulfilled_document_id?: string | null
}

export interface ContextTask {
  id: string
  task_type: string
  work_kind?: string
  priority?: string | null
  status: string
  target_kind?: string | null
  target_id?: string | null
  requirement_id?: string | null
  document_ids?: readonly string[]
  fund_id?: string | null
}

export interface ContextDocumentFact {
  reference: DocumentRef
  status?: string
  source_name?: string | null
  source_kind?: string | null
}

export interface ContextContribution {
  id: string
  document_id: string
  source_kind: string
  source_id: string
  target?: ContextAnchor | null
  proposal_id?: string | null
  requirement_id?: string | null
  request_id?: string | null
  task_id?: string | null
  evidence_claim?: boolean | null
}

export interface ContextWorkPayload extends Omit<
  ContextWork,
  'documents' | 'requirements' | 'tasks'
> {
  documents: readonly ContextDocumentFact[]
  requirements: readonly ContextRequirement[]
  tasks: readonly ContextTask[]
  proposals?: readonly ContextProposal[]
  requests?: readonly ContextRequest[]
  contributions?: readonly ContextContribution[]
  objects?: readonly {
    anchor: ContextAnchor
    label?: string | null
    fields?: Record<string, unknown>
    record_state?: string
  }[]
}

export interface ContextQueueView {
  name: ContextQueue
  card_ids: readonly string[]
  counts: QueueCounts
}

export interface ContextCardPayload extends WorkCard {
  proposal_ids?: readonly string[]
  resolved_requirement_ids?: readonly string[]
}

export interface ContextCardView {
  card: WorkCard | BookDiscoveryCard
  queues: readonly ContextQueue[]
  proposals: readonly ContextProposal[]
  requirements: readonly ContextRequirement[]
  requests: readonly ContextRequest[]
  tasks: readonly ContextTask[]
  documents: readonly DocumentRef[]
  contributions: readonly ContextContribution[]
  bookReview?: BookReview
  removalRequests: readonly RemovalWork[]
}

export interface ContextWorkView {
  queues: readonly ContextQueueView[]
  cards: readonly ContextCardView[]
}

const QUEUES: readonly ContextQueue[] = ['book', 'evidence', 'chase']

function ids(values: readonly (string | { toString(): string })[] | undefined): string[] {
  return (values ?? []).map((value) => String(value))
}

function asPayload(work: ContextWork): ContextWorkPayload {
  return work as unknown as ContextWorkPayload
}

function cardMembers(card: WorkCard): Set<string> {
  return new Set(card.members.map((member) => `${member.kind}:${member.id}`))
}

function includesTarget(target: ContextAnchor | null | undefined, members: Set<string>): boolean {
  return !!target && members.has(`${target.kind}:${target.id}`)
}

function cardDocuments(
  work: ContextWorkPayload,
  card: WorkCard,
  extraDocumentIds: readonly string[] = []
): readonly DocumentRef[] {
  const wanted = new Set([...ids(card.document_ids), ...extraDocumentIds.map(String)])
  for (const contribution of work.contributions ?? []) {
    if (contribution.target && includesTarget(contribution.target, cardMembers(card)))
      wanted.add(String(contribution.document_id))
  }
  const seen = new Set<string>()
  const documents: DocumentRef[] = []
  for (const document of work.documents ?? []) {
    if (
      !wanted.has(String(document.reference.document_id)) ||
      seen.has(String(document.reference.document_id))
    )
      continue
    seen.add(String(document.reference.document_id))
    documents.push(document.reference)
  }
  return documents
}

function cardBookReview(work: ContextWorkPayload, card: WorkCard): BookReview | undefined {
  const extended = card as WorkCard & {
    book_review?: BookReview
    bookReview?: BookReview
  }
  const reviews = (
    work as ContextWorkPayload & {
      book_reviews?: Record<string, BookReview>
    }
  ).book_reviews
  return extended.book_review ?? extended.bookReview ?? reviews?.[card.id]
}

export function queueViews(work: ContextWork): readonly ContextQueueView[] {
  const payload = asPayload(work)
  return QUEUES.map((name) => {
    const queue = payload.queues.find((item) => item.name === name)
    return {
      name,
      card_ids: queue?.card_ids ?? [],
      counts: queue?.counts ?? {
        card_count: 0,
        proposal_count: 0,
        requirement_count: 0,
        request_count: 0,
      },
    }
  })
}

export function contextWorkView(work: ContextWork | null | undefined): ContextWorkView {
  if (!work) return { queues: [], cards: [] }
  const payload = asPayload(work)
  const queues = queueViews(work)
  const queueNames = new Map<string, ContextQueue[]>()
  for (const queue of queues)
    for (const cardId of queue.card_ids)
      queueNames.set(cardId, [...(queueNames.get(cardId) ?? []), queue.name])
  const cards = (payload.cards as readonly ContextCardPayload[]).map((card): ContextCardView => {
    const memberKeys = cardMembers(card)
    const proposalIds = new Set(ids(card.proposal_ids ?? card.book_proposal_ids))
    const requirementIds = new Set([
      ...ids(card.evidence_requirement_ids),
      ...ids(card.chase_requirement_ids),
      ...ids(card.resolved_requirement_ids),
    ])
    const proposals = (payload.proposals ?? []).filter((proposal) =>
      proposalIds.has(String(proposal.id))
    )
    const requirements = (payload.requirements ?? []).filter((requirement) =>
      requirementIds.has(String(requirement.id))
    )
    const requirementIdSet = new Set(requirements.map((requirement) => String(requirement.id)))
    const requests = (payload.requests ?? []).filter((request) =>
      requirements.some((requirement) =>
        (requirement.request_ids ?? []).some((id) => String(id) === String(request.id))
      )
    )
    const requestIds = new Set(requests.map((request) => String(request.id)))
    const tasks = (payload.tasks ?? []).filter(
      (task) =>
        (card.close_task_ids ?? []).includes(task.id) ||
        (task.target_kind === 'document_request' &&
          !!task.target_id &&
          requestIds.has(String(task.target_id))) ||
        (task.requirement_id && requirementIdSet.has(String(task.requirement_id))) ||
        (task.target_kind &&
          task.target_id &&
          memberKeys.has(`${task.target_kind}:${task.target_id}`))
    )
    const contributions = (payload.contributions ?? []).filter(
      (contribution) =>
        (contribution.proposal_id && proposalIds.has(String(contribution.proposal_id))) ||
        (contribution.requirement_id &&
          requirementIdSet.has(String(contribution.requirement_id))) ||
        includesTarget(contribution.target, memberKeys)
    )
    return {
      card,
      queues: queueNames.get(card.id) ?? [],
      proposals,
      removalRequests: (payload.removal_requests ?? []).filter((row) =>
        (card.removal_request_ids ?? []).includes(row.id)
      ),
      requirements,
      requests,
      tasks,
      documents: cardDocuments(payload, card, [
        ...requirements.flatMap((requirement) => [...(requirement.linked_document_ids ?? [])]),
        ...tasks.flatMap((task) => [...(task.document_ids ?? [])]),
        ...requests.flatMap((request) =>
          request.fulfilled_document_id ? [request.fulfilled_document_id] : []
        ),
      ]),
      contributions,
      bookReview: cardBookReview(payload, card),
    }
  })
  for (const card of payload.book_cards ?? []) {
    if (!queueNames.get(card.id)?.includes('book')) continue
    const proposalIds = new Set(card.proposal_ids)
    const documentIds = new Set(card.document_ids)
    cards.push({
      card,
      queues: ['book'],
      proposals: (payload.proposals ?? []).filter(
        (proposal) => proposal.group_id === card.group_id && proposalIds.has(proposal.id)
      ),
      requirements: [],
      requests: [],
      tasks: [],
      removalRequests: [],
      documents: payload.documents
        .filter((document) => documentIds.has(document.reference.document_id))
        .map((document) => document.reference),
      contributions: (payload.contributions ?? []).filter(
        (contribution) => !!contribution.proposal_id && proposalIds.has(contribution.proposal_id)
      ),
    })
  }
  return { queues, cards }
}

function objectLabel(
  work: ContextWorkPayload,
  kind: string,
  id: string | null | undefined
): string | null {
  if (!id) return null
  const object = (work.objects ?? []).find(
    (candidate) => candidate.anchor.kind === kind && String(candidate.anchor.id) === String(id)
  )
  return object?.label ?? null
}

export function cardContextLabel(work: ContextWork, card: WorkCard | BookDiscoveryCard): string {
  if (isBookDiscoveryCard(card)) return card.group_id ? 'Proposed changes' : 'Document review'
  const payload = asPayload(work)
  const identity = card.identity
  if (identity.kind === 'subchain') {
    const parts = [
      objectLabel(payload, 'entity', identity.holder_id),
      objectLabel(payload, 'instrument', identity.instrument_id),
      objectLabel(payload, 'entity', identity.issuer_id),
    ].filter((value): value is string => !!value)
    if (parts.length >= 2) return parts.join(' · ')
  }
  if (identity.kind === 'instrument') {
    const instrument = objectLabel(payload, 'instrument', identity.instrument_id) ?? card.label
    const issuer = objectLabel(payload, 'entity', identity.issuer_id)
    return issuer ? `${instrument} · ${issuer}` : `Instrument context · ${instrument}`
  }
  const entity = objectLabel(payload, 'entity', identity.entity_id) ?? card.label
  return `Entity context · ${entity}`
}

export function scopeLabel(
  scope: ViewScope,
  context: {
    client?: { display_name: string | null } | null
    records?: readonly { record: { fund_name?: string } }[]
    work: ContextWork
  }
): string {
  if (scope.kind === 'client') return context.client?.display_name ?? 'Selected client'
  if (scope.kind === 'fund') return context.records?.[0]?.record.fund_name ?? 'Selected fund'
  if (scope.kind === 'subchain') {
    const card = context.work.cards.find((item) => item.id === scope.subchain_id)
    return card ? cardContextLabel(context.work, card) : 'Selected subchain'
  }
  return 'All client funds'
}

/** Show only live tasks belonging to this card's selected queue. */
export function tasksForQueue(item: ContextCardView, queue: ContextQueue): readonly ContextTask[] {
  if (isBookDiscoveryCard(item.card)) return []
  const structuralCard = item.card
  const requirementIds = new Set(
    item.requirements.filter((row) => row.queue === queue).map((row) => row.id)
  )
  const requestIds = new Set(
    item.requests
      .filter((row) => row.requirement_id && requirementIds.has(row.requirement_id))
      .map((row) => row.id)
  )
  return item.tasks.filter((task) => {
    if (task.status !== 'open') return false
    if (task.work_kind === 'subchain_close')
      return queue === 'evidence' && (structuralCard.close_task_ids ?? []).includes(task.id)
    if (task.task_type === 'evidence_approval')
      return (
        queue === 'evidence' && !!task.requirement_id && requirementIds.has(task.requirement_id)
      )
    if (task.task_type === 'investigation_action')
      return !!task.requirement_id && requirementIds.has(task.requirement_id)
    if (task.task_type === 'request_overdue')
      return (
        queue === 'chase' &&
        task.target_kind === 'document_request' &&
        !!task.target_id &&
        requestIds.has(task.target_id)
      )
    return false
  })
}

export function requirementTargetLabel(work: ContextWork, requirement: ContextRequirement): string {
  return (
    objectLabel(asPayload(work), requirement.target.kind, requirement.target.id) ??
    requirement.target.kind[0].toUpperCase() + requirement.target.kind.slice(1)
  )
}
